<script>
import Layout from "../../../layouts/main";
import Multiselect from "vue-multiselect";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import moment from "moment";
import { required, requiredIf } from "vuelidate/lib/validators";
import {
  CREATE_NTUC_REPORT,
  FETCH_NTUC_MID_CONFIGS,
} from "@/state/actions.type";

export default {
  components: {
    Layout,
    Multiselect,
    DatePicker,
  },
  validations: {
    form: {
      reconType: { required },
      mid: {
        required: requiredIf(function () {
          return this.showMidDropdown;
        }),
      },
      month: { required },
    },
  },
  data() {
    return {
      form: {
        reconType: null,
        mid: null,
        month: null,
      },
      reconTypeOptions: [
        { key: "daily_cc", text: "MPGS CC Recon" },
        { key: "monthly_ccr", text: "MPGS Batch" },
        // { key: "giro_monthly", text: "GIRO Batch", $isDisabled: true },
      ],
      isSubmitting: false,
      showSuccessModal: false,
      successMonth: "",
    };
  },

  computed: {
    midConfigs() {
      return this.$store.state.ntucRecon.midConfigs;
    },
    filteredMids() {
      if (!this.form.reconType) return [];
      return this.midConfigs.filter(
        (config) => config.recon_type === this.form.reconType.key
      );
    },
    showMidDropdown() {
      return this.form.reconType && this.form.reconType.key === "daily_cc";
    },
  },

  watch: {
    "form.reconType"(newVal) {
      this.form.mid = null;
      if (newVal && newVal.key === "monthly_ccr") {
        const ccr_mids = this.midConfigs.filter(
          (c) => c.recon_type === "monthly_ccr"
        );
        if (ccr_mids.length === 1) {
          this.form.mid = ccr_mids[0];
        }
      }
    },
  },

  created() {
    this.$store.dispatch(`ntucRecon/${FETCH_NTUC_MID_CONFIGS}`);
  },

  methods: {
    async submitForm() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;

      this.isSubmitting = true;

      const monthStr = moment(this.form.month).format("YYYY-MM");

      const payload = {
        mid: this.form.mid.mid,
        month: monthStr,
      };

      try {
        await this.$store.dispatch(`ntucRecon/${CREATE_NTUC_REPORT}`, payload);
        this.successMonth = moment(this.form.month).format("MMMM, YYYY");
        this.showSuccessModal = true;
      } catch (error) {
        console.error("Report generation failed:", error);
      } finally {
        this.isSubmitting = false;
      }
    },

    goToReports() {
      this.showSuccessModal = false;
      this.$router.push("/reconciliation/ntuc/reports");
    },
  },
};
</script>

<template>
  <Layout>
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <router-link
          to="/reconciliation/ntuc/reports"
          class="text-chai d-inline-block mb-2"
          style="font-size: 14px;"
        >
          <i class="mdi mdi-arrow-left mr-1"></i> Back to Reports
        </router-link>
        <h4 class="mb-0 font-size-18">Create Reconciliation</h4>
        <p class="text-muted mb-0" style="font-size: 14px;">
          Select Type, Merchant ID, and Month to generate your report.
        </p>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-6">
        <div class="common-border bg-white" style="padding: 25px;">
          <div class="mb-4">
            <label style="font-size: 14px;">Type of Reconciliation</label>
            <multiselect
              v-model="form.reconType"
              :options="reconTypeOptions"
              label="text"
              track-by="key"
              placeholder="Choose reconciliation type..."
              :allow-empty="false"
              :class="{ 'is-invalid': $v.form.reconType.$error }"
              class="custom-multiselect"
            />
            <div v-if="$v.form.reconType.$error" class="invalid-feedback d-block">
              Please select a reconciliation type.
            </div>
          </div>

          <div v-if="showMidDropdown" class="mb-4">
            <label style="font-size: 14px;">Merchant ID (MID)</label>
            <multiselect
              v-model="form.mid"
              :options="filteredMids"
              :custom-label="(opt) => `${opt.mid} - ${opt.label}`"
              track-by="mid"
              placeholder="Select MID..."
              :allow-empty="false"
              :class="{ 'is-invalid': $v.form.mid.$error }"
              class="custom-multiselect"
            />
            <div v-if="$v.form.mid.$error" class="invalid-feedback d-block">
              Please select a Merchant ID.
            </div>
          </div>

          <div
            v-if="form.reconType && form.reconType.key === 'monthly_ccr' && form.mid"
            class="mb-4"
          >
            <label style="font-size: 14px;">Merchant ID (MID)</label>
            <div class="common-border d-flex align-items-center" style="padding: 0 15px; min-height: 44px; font-size: 14px; background: rgba(220, 218, 213, 0.15);">
              {{ form.mid.mid }} - {{ form.mid.label }}
            </div>
          </div>

          <div class="mb-4">
            <label style="font-size: 14px;">Month and Year</label>
            <date-picker
              v-model="form.month"
              type="month"
              format="MMMM, YYYY"
              placeholder="Select month..."
              class="w-100"
              prefix-class="xmx"
              :class="{ 'is-invalid': $v.form.month.$error }"
            />
            <div v-if="$v.form.month.$error" class="invalid-feedback d-block">
              Please select a month.
            </div>
          </div>

          <b-btn
            variant="primary"
            block
            :disabled="isSubmitting"
            @click="submitForm"
            class="btn-padding"
            style="height: 44px;"
          >
            <b-spinner v-if="isSubmitting" small class="mr-1"></b-spinner>
            Proceed
          </b-btn>
        </div>
      </div>
    </div>

    <b-modal
      v-model="showSuccessModal"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
    >
      <div class="text-center py-4">
        <div
          class="d-inline-flex align-items-center justify-content-center mb-3"
          style="width: 56px; height: 56px; border-radius: 50%; background: rgba(33, 150, 83, 0.10);"
        >
          <i class="mdi mdi-check-circle" style="font-size: 28px; color: #219653;"></i>
        </div>
        <h5 class="font-weight-bold">Report Generated</h5>
        <p class="text-muted" style="font-size: 14px;">
          Generated Reconciliation Report for {{ successMonth }}.
        </p>
        <b-btn variant="primary" block @click="goToReports" class="btn-padding" style="height: 44px;">
          View Reports
        </b-btn>
      </div>
    </b-modal>
  </Layout>
</template>

<style scoped>
::v-deep .xmx-input {
  padding: 13px;
  padding-left: 35px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid #DCDAD5;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: #000;
}
::v-deep .xmx-datepicker {
  width: 100%;
}
::v-deep .xmx-icon-calendar {
  left: 10px;
}
</style>
